import { getAccountDetails, getCalculatedAccountDetails, newGetAccountDetails, getMonthsBetweenDates, numberToColumn, numberWithCommas } from "./utils";
import formula from 'excel-formula';
import { evaluate } from 'mathjs';
import { parse, format } from 'date-fns';
import { debugLogger } from './debugLogger';
import { OPERATORS } from './constants';

export const currencyFormatter = (params) => {
	if (params.value === null || params.value === undefined || params.value === '') {
		return '';
	}
	const rawValue = Number(params.value.toString().replace(/[ ,]/g, ""));
	if (isNaN(rawValue)) {
		return '';
	}
	const roundedValue = Math.round(rawValue);
	if (roundedValue === 0) {
		return '';
	}
	return numberWithCommas(roundedValue);
}

export const accountNameRenderer = (params) => {
  // console.log('params', params);
  // return params;
}

export const getRowId = params => params.data.glKey;

/**
 * @param {String} date Month of desired change
 * @param {Object} account The account being changed
 * @param {Object} change The change being applied
 * @param {Object} treeData Tree data for the active project
 * @returns Returns a calculated change for a single month
 */
// export const getSingleMonthChange = (date, account, change, treeData) => { // TODO: Improve this function to match new data model
//   const formattedDate = moment(date).format('MMM YYYY');
// 	const basePeriod = account.dates[formattedDate];
// 	const {
// 		function_operator, operator_value, reference_accounts, ref_account_formula
// 	} = change;
// 	let changedValue;

// 	if (function_operator === 'percentage') {
// 		changedValue = basePeriod * Number(operator_value) / 100;
// 	} else if (function_operator === 'absoluteValue') {
// 		changedValue = Number(operator_value);
// 	} else {
// 		let referenceAccountChangeSum = 0;
//     let changeAmount = formula.toJavaScript(ref_account_formula);
//     changeAmount = changeAmount.replace('A', parseInt(account.dates[date]?.replace(',', '')));
//     // Loop through each reference account and replace the corresponding row label with the reference accounts value for the given date
//     reference_accounts.forEach((refAccount, i) => {
//       const refAccountDetails = getAccountDetails(refAccount.glKey, treeData);
//       const rowLetter = refAccount.rowLetter;
//       if(refAccountDetails){
//         const initialRefAccountValue = refAccountDetails.dates[date].replace(',', '');
//         changeAmount = changeAmount.replaceAll(rowLetter, initialRefAccountValue);
//       }
//     });
//     changeAmount = mathjs.evaluate(changeAmount);
//     referenceAccountChangeSum += changeAmount;
// 		changedValue = referenceAccountChangeSum;
// 	}
//   if(changedValue === 0){
//     return '';
//   }
// 	return changedValue;
// }

/**
 * Calculate monthly values for a calculated account based on reference accounts and formula
 * @param {Object} change - The change configuration containing formula and reference accounts
 * @param {Object} treeData - Tree structure containing account data
 * @param {Object} agGridData - Grid data for calculated accounts
 * @param {Object[]} changes - Array of all changes that might affect this calculation
 * @returns {Object} Account data with monthly values and base period dates
 */
export const getCalculatedAccountValues = (change, treeData, agGridData, changes) => {
  const accountData = initializeAccountData();
  const referenceAccountKeys = extractReferenceAccountKeys(change);
  const relevantChanges = findRelevantChanges(changes, change.id, referenceAccountKeys);
  const dateRange = getMonthsBetweenDates(change.date_range[0], change.date_range[1]);

  dateRange.forEach(month => {
    try {
      const monthlyValue = calculateMonthlyValue(
        month, 
        change, 
        treeData, 
        agGridData, 
        relevantChanges
      );
      
      accountData[month] = monthlyValue;
      accountData.basePeriodDates[month] = monthlyValue;
    } catch (error) {
      console.error(`Error calculating value for month ${month}:`, error);
      return { error: `Calculation failed for ${month}: ${error.message}` };
    }
  });

  return accountData;
};

/**
 * Initialize the account data structure
 */
function initializeAccountData() {
  return {
    basePeriodDates: {}
  };
}

/**
 * Extract GL keys from reference accounts
 */
function extractReferenceAccountKeys(change) {
  return (change.reference_accounts || []).map(account => account.glKey);
}

/**
 * Find changes that affect the reference accounts of this calculated account
 */
function findRelevantChanges(allChanges, currentChangeId, referenceAccountKeys) {
  const relevantChanges = [];
  
  allChanges.forEach(change => {
    if (change.id === currentChangeId) return;
    
    const hasRelevantAccount = change.selected_accounts.some(account => 
      referenceAccountKeys.includes(account)
    );
    
    if (hasRelevantAccount) {
      change.selected_accounts.forEach(account => {
        relevantChanges.push({
          changeId: change.id,
          accountKey: account,
          changeData: change
        });
      });
    }
  });
  
  return relevantChanges;
}

/**
 * Calculate the value for a specific month
 */
function calculateMonthlyValue(month, change, treeData, agGridData, relevantChanges) {
  let formulaString = formula.toJavaScript(change.ref_account_formula);
  let additionalChanges = 0;

  change.reference_accounts.forEach(refAccount => {
    const accountValue = getAccountValueForMonth(
      refAccount, 
      month, 
      treeData, 
      agGridData
    );
    
    // Replace the row letter in formula with actual value
    formulaString = formulaString.replaceAll(refAccount.rowLetter, accountValue);
    
    // Add any relevant changes for this account
    additionalChanges += calculateAdditionalChanges(
      refAccount.glKey, 
      month, 
      relevantChanges, 
      treeData
    );
  });

  try {
    const calculatedValue = evaluate(formulaString);
    return calculatedValue + additionalChanges;
  } catch (error) {
    console.warn('Math evaluation error in evaluateReferenceForMonth:', error.message);
    console.warn('Formula string:', formulaString);
    // Return just additional changes if formula evaluation fails
    return additionalChanges;
  }
}

/**
 * Get the account value for a specific month
 */
function getAccountValueForMonth(refAccount, month, treeData, agGridData) {
  const isCalculatedAccount = !refAccount.glKey || refAccount.glKey.length < 5 || refAccount.isCalcAccount;
  
  if (isCalculatedAccount) {
    const accountDetails = getCalculatedAccountDetails(refAccount.glKey, agGridData);
    return accountDetails[month] || 0;
  } else {
    const accountDetails = getAccountDetails(refAccount.glKey, treeData);
    if (!accountDetails || !accountDetails.dates[month]) {
      throw new Error(`Account data not found for ${refAccount.glKey} in ${month}`);
    }
    return parseFloat(accountDetails.dates[month].replace(',', '')) || 0;
  }
}

/**
 * Calculate additional changes that affect this account for the given month
 */
function calculateAdditionalChanges(accountKey, month, relevantChanges, treeData) {
  let totalAdditionalChanges = 0;
  
  const accountChanges = relevantChanges.filter(change => 
    change.accountKey === accountKey
  );
  
  accountChanges.forEach(change => {
    const accountDetails = getAccountDetails(accountKey, treeData);
    if (accountDetails) {
      const changeAmount = getSingleMonthChange(
        month, 
        accountDetails, 
        change.changeData, 
        treeData
      );
      totalAdditionalChanges += changeAmount;
    }
  });
  
  return totalAdditionalChanges;
}

/**
 * Calculates a ratio-driven change for an account driven by a stat account, based on how much the stat account was changed.
 * @param {Object} change A change that must be a stat driver (is_stat_driver: true)
 * @param {String} account GL Key of the driving account
 * @param {Object} treeData Active project object of all Entities, Departments, and Accounts
 * @returns Object containing the details of the change to the account specified
 */
export const getStatDriverChanges = (change, account,projectId,accessToken) => {

	const showStatDriverDebugging = false;

  if(showStatDriverDebugging){
    // console.log('change', change);
    // console.log('account', account);
  }

	let changes = [];
	const driven_accounts = change.driven_accounts.filter((acc) => {
		let parsedGlKey = acc.split("-");
		if(parsedGlKey[1] === account.department_id){
			return acc;
		}
    return false;
	});

	driven_accounts.forEach((drivenAccount, i) => {
		let drivenAccountDetails = newGetAccountDetails(projectId,drivenAccount,accessToken);
    
  
		changes[i] = {
			dates: {},
			glKey: drivenAccount,
			total: '',
		};
		let newTotal = 0;
    const dateRange = getMonthsBetweenDates(change.date_range[0], change.date_range[1]);
		dateRange.forEach((month) => {
      const formattedMonth = month; // Already in 'MMM yyyy' format from getMonthsBetweenDates
			const baseChange = getSingleMonthChange(formattedMonth, account, change,projectId,accessToken);
			const statDriverBase = account.dates[formattedMonth];
			const drivenAccountValue = drivenAccountDetails.dates[formattedMonth];
			const ratio = drivenAccountValue/statDriverBase;
			const ratioDrivenChange = ratio*baseChange;
			
			if(showStatDriverDebugging && i === 0){
				// console.log("statDriverChange -------------------------");
				// console.log(baseChange);
				// console.log(statDriverBase);
				// console.log(drivenAccountValue);
				// console.log(ratio);
				// console.log(ratioDrivenChange);
			}
			
			changes[i]['dates'][formattedMonth] = ratioDrivenChange;
			newTotal += ratioDrivenChange;
		});
		changes[i].total = newTotal;
		
	});
	// if(showStatDriverDebugging){ console.log(changes); }
	return changes;
}

/**
   * Calculates the value of a Dominant Change. The displayed value is whatever delta
   * is needed so that:
   *
   *   basePeriod + standardChangesTotal + dominantDelta = basePeriod + dominantEntryValue
   *
   * i.e.  dominantDelta = dominantEntryValue - standardChangesTotal
   *
   * This means the dominant change absorbs all other changes and still lands the
   * forecast at the user's intended target.
   *
   * Only one Dominant Change is allowed per GL key.
   *
   * @param {Object} rowData  AG Grid row data for this account (has basePeriodDates, changes, and pre-keyed standard change values)
   * @param {Object} change   The dominant change object
   * @param {number} standardDeltaForMonth  Pre-computed sum of all standard (non-dominant) change deltas for this month.
   *                                        Pass this in so the caller controls ordering — standard changes must be
   *                                        summed before this is called.
   * @returns {Object} Map of month string → dominant change delta
   */
  export const getDominantChange = (rowData, change, standardDeltaByMonth = {}) => {
    const dates = getMonthsBetweenDates(change.date_range[0], change.date_range[1]);
    const changeData = {};

    dates.forEach((date) => {
      const entryDelta = getSingleMonthChange(date, { data: rowData }, change);
      if (entryDelta === '') {
        changeData[date] = 0;
        return;
      }

      const standardTotal = standardDeltaByMonth[date] || 0;

      // Target forecast = base + entryDelta
      // Current forecast (without dominant) = base + standardTotal
      // Dominant delta needed = target - current = entryDelta - standardTotal
      const dominantDelta = entryDelta - standardTotal;

      changeData[date] = dominantDelta;
    });

    return changeData;
  }


/** --------------
 * 
 * Value Getters
 * 
 * --------------*/

/**
 * Evaluates reference account formulas for a single month
 * @param {number} primaryValue - The primary value (A) for the formula
 * @param {string} dateKey - 'YYYY-MM-DD' date key for this month
 * @param {Object[]} referenceAccounts - Array of reference account objects
 * @param {string} referenceFormula - The formula to evaluate
 * @returns {number} The evaluated amount
 */
export const evaluateReferenceForMonth = (primaryValue, dateKey, referenceAccounts, referenceFormula) => {
  const variables = [{
    rowLetter: 'A',
    variableValue: primaryValue,
  }];

  referenceAccounts.forEach((refAccount) => {
    const variableValue = refAccount.dates ? (Number(refAccount.dates[dateKey]) || 0) : 0;
    variables.push({
      rowLetter: refAccount.rowLetter,
      variableValue: variableValue,
    });
  });

  let changeAmount = formula.toJavaScript(referenceFormula);
  variables.forEach((variable) => {
    changeAmount = changeAmount.replaceAll(
      variable.rowLetter,
      variable.variableValue.toString()
    );
  });

  try {
    const evaluatedAmount = evaluate(changeAmount);
    return Number(evaluatedAmount) || 0;
  } catch (error) {
    console.warn('Math evaluation error in evaluateReferenceForMonth:', error.message);
    console.warn('Change amount formula:', changeAmount);
    return 0;
  }
};

/**
 * Computes the changed value for a single account for a specific month
 * @param {string} accountKey - The account identifier in changeDates
 * @param {number} baseValue - The base value for this account/month
 * @param {string} date - Month string in 'MMM yyyy' format
 * @param {string} operator - The function operator type
 * @param {number|string} operatorValue - The operator value
 * @param {Object} changeDates - Object containing date values for accounts
 * @param {Object[]} referenceAccounts - Array of reference account objects
 * @param {string} referenceFormula - The formula to evaluate
 * @returns {number} The changed value for this month for that account
 */
export const computeSingleAccountMonthChange = (
  accountKey, 
  baseValue, 
  date, 
  operator, 
  operatorValue, 
  changeDates, 
  referenceAccounts, 
  referenceFormula
) => {
  const parsedDate = parse(date, 'MMM yyyy', new Date());
  const dateKey = format(parsedDate, 'yyyy-MM-dd');

  // changeDates[accountKey][dateKey] is the base period value for this account/month
  // (populated by the backend from projects_data_accounts.dates).
  // We use it as the authoritative base when available; fall back to baseValue otherwise.
  //
  // NOTE: accountMonthBase and baseValue are equivalent when the caller passes the
  // account's raw base-period value as baseValue (the normal case). They can diverge
  // if a caller passes a value that has already been modified by a prior change.
  // All current callers pass the unmodified base period, so the results are consistent.
  // If stacked-change semantics are ever needed, callers should pass the accumulated
  // value as baseValue and ensure changeDates is updated accordingly.
  const accountMonthBase = (changeDates && changeDates[accountKey] && changeDates[accountKey][dateKey] != null)
    ? Number(changeDates[accountKey][dateKey])
    : baseValue;

  switch (operator) {
    case 'percentage':
      // Return the new value: base * (1 + rate)
      return accountMonthBase * (1 + (Number(operatorValue) || 0) / 100);

    case 'absoluteValue':
      // Return the new value: base + absolute amount
      return accountMonthBase + Number(operatorValue);

    case 'referenceAccount':
      return evaluateReferenceForMonth(baseValue, dateKey, referenceAccounts, referenceFormula);

    default:
      return baseValue;
  }
};

/**
 * Gets the base period value for a given date from row data
 * @param {Object} rowData - The row data object
 * @param {string} date - Month string in 'MMM yyyy' format
 * @returns {number} The base period value
 */
export const getBasePeriodValue = (rowData, date) => {
  let basePeriod = rowData[date];

  // If not found, convert date to 'yyyy-MM-dd' format
  if (typeof basePeriod === 'undefined') {
    const parsedDate = parse(date, 'MMM yyyy', new Date());
    const dateKey = format(parsedDate, 'yyyy-MM-dd');
    basePeriod = rowData[dateKey];
  }

  return Number(basePeriod) || 0;
};

/**
 * Calculates single month change using flat arguments (new reusable method)
 * @param {string} date - Month of desired change
 * @param {string} accountId - The account ID
 * @param {string[]} childAccounts - Unused; kept for call-site compatibility. Child
 *   account aggregation was removed in the eager-loading refactor — each account is
 *   now looked up directly. Pass [] at all call sites.
 * @param {Object} rowData - The row data object containing month values
 * @param {string[]} selectedAccounts - Array of selected account IDs
 * @param {string} operator - The function operator type
 * @param {number|string} operatorValue - The operator value
 * @param {Object} changeDates - Object containing date values for accounts
 * @param {Object[]} referenceAccounts - Array of reference account objects
 * @param {string} referenceFormula - The formula to evaluate
 * @returns {string|number} Calculated change delta or empty string if account not selected
 */
export const calculateSingleMonthChange = (
  date,
  accountId,
  childAccounts = [], // eslint-disable-line no-unused-vars
  rowData,
  selectedAccounts,
  operator,
  operatorValue,
  changeDates,
  referenceAccounts = [],
  referenceFormula = ''
) => {
  if (!rowData) {
    return '';
  }

  const basePeriod = getBasePeriodValue(rowData, date);
  const isSelectedAccount = selectedAccounts.includes(accountId);

  if (!isSelectedAccount) {
    return '';
  }

  // computeSingleAccountMonthChange returns the new value (base * (1 + rate) for percentage).
  // Subtract basePeriod to get the delta that the change column should display.
  const newValue = computeSingleAccountMonthChange(
    accountId,
    basePeriod,
    date,
    operator,
    operatorValue,
    changeDates,
    referenceAccounts,
    referenceFormula
  );

  const delta = newValue - basePeriod;

  return delta;
};

/**
 * Original getSingleMonthChange function (wrapper for backward compatibility)
 * @param {string} date - Month of desired change
 * @param {Object} params - AG Grid parameters containing data
 * @param {Object} change - The change being applied
 * @returns {string|number} Calculated change or empty string if no changes apply
 */
export const getSingleMonthChange = (date, params, change) => {
  if (!params?.data) {
    return '';
  }

  // For dominant changes, calculateDominantChanges pre-stores the adjusted delta
  // keyed as `${change.id} ${date}`. Check that first so the change column displays
  // the correct adjusted value rather than the raw entry delta.
  if (change.is_dominant_change) {
    const dominantFieldName = `${change.id} ${date}`;
    if (params.data[dominantFieldName] !== undefined && params.data[dominantFieldName] !== null) {
      return params.data[dominantFieldName];
    }
  }

  // For non-dominant changes (and dominant changes before pre-computation runs),
  // check if the value is already stored under change_label key.
  // This is the case for child stats under Master Stats.
  const changeFieldName = `${change.change_label} ${date}`;
  if (params.data[changeFieldName] !== undefined && params.data[changeFieldName] !== null) {
    return params.data[changeFieldName];
  }

  const {
    function_operator: operator,
    operator_value: operatorValue,
    reference_accounts: referenceAccounts,
    ref_account_formula: referenceFormula,
    dates: changeDates,
    selected_accounts: selectedAccounts
  } = change;

  // Check if this change has monthly_values in reference_accounts
  if (referenceAccounts && Array.isArray(referenceAccounts)) {
    const accountGlKey = params.data.id;

    // Find the reference account that matches this row's glKey
    const matchingRefAccount = referenceAccounts.find(refAcct => refAcct.glKey === accountGlKey);

    // If we found a matching reference account with monthly_values, use them
    if (matchingRefAccount && matchingRefAccount.monthly_values && Array.isArray(matchingRefAccount.monthly_values)) {
      const monthValue = matchingRefAccount.monthly_values.find(mv => mv.month_year === date);

      if (monthValue) {
        // If it's a formula, we would need to evaluate it
        // For now, just return the value
        if (monthValue.is_formula && monthValue.formula_text) {
          // TODO: Evaluate formula if needed
          return monthValue.value || 0;
        } else if (monthValue.value !== null && monthValue.value !== undefined) {
          return monthValue.value;
        }
      }
    }
  }

  // Fall back to the original calculation logic
  return calculateSingleMonthChange(
    date,
    params.data.id,
    params.data.child_accounts || [],
    params.data,
    selectedAccounts,
    operator,
    operatorValue,
    changeDates,
    referenceAccounts,
    referenceFormula
  );
};

/**
 * @param {Object} params AG Grid parameters for a row
 * @returns Total value for the Base Period column
 */
export const basePeriodTotal = (params, dateRange) => {
  if(params.data){
    let basePeriod = params.data
    const dateRegex = /^[A-Za-z]{3} \d{4}$/
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

    // If a date range filter is active, compute its numeric bounds once
    const fromNum = dateRange ? dateRange.from.year * 100 + dateRange.from.month : null;
    const toNum   = dateRange ? dateRange.to.year   * 100 + dateRange.to.month   : null;

    let total = 0
    Object.keys(basePeriod).forEach((key) => {
      if (dateRegex.test(key)) {
        // If filtering, skip months outside the range
        if (fromNum !== null) {
          const m = key.match(/^([A-Za-z]{3}) (\d{4})$/);
          if (m) {
            const n = Number(m[2]) * 100 + (monthNames.indexOf(m[1]) + 1);
            if (n < fromNum || n > toNum) return;
          }
        }
        total += Number(basePeriod[key])
      }
    })
    return total
  }
}

/**
 * Converts month string to date key format
 * @param {string} monthStr - Month string in 'MMM yyyy' format
 * @returns {string} Date key in 'yyyy-MM-dd' format
 */
export const convertMonthToDateKey = (monthStr) => {
  const parsedDate = parse(monthStr, 'MMM yyyy', new Date());
  return format(parsedDate, 'yyyy-MM-dd');
};

/**
 * Calculates total value for an account across all dates
 * @param {string} accountKey - The account identifier
 * @param {string[]} dateList - Array of date strings
 * @param {Object} changeDates - Object containing date values for accounts
 * @returns {number} Total value across all dates
 */
export const calculateAccountTotal = (accountKey, dateList, changeDates) => {
  if (!changeDates) {
    return 0;
  }
  return dateList.reduce((total, monthStr) => {
    const dateKey = convertMonthToDateKey(monthStr);
    const value = (changeDates && changeDates[accountKey] && changeDates[accountKey][dateKey]) || 0;
    return total + Number(value);
  }, 0);
};

/**
 * Evaluates reference account formula with variables
 * @param {number} primarySum - The primary account sum
 * @param {string[]} dateList - Array of date strings
 * @param {Object[]} referenceAccounts - Array of reference account objects
 * @param {string} referenceFormula - The formula to evaluate
 * @returns {number} Evaluated formula result
 */
export const evaluateReferenceFormula = (primarySum, dateList, referenceAccounts, referenceFormula) => {
  // Build variables array starting with primary account (A)
  const variables = [];

  // Add reference account variables
  referenceAccounts.forEach(refAccount => {
    const variableValue = dateList.reduce((total, monthStr) => {
      const dateKey = convertMonthToDateKey(monthStr);
      const value = (refAccount.dates && refAccount.dates[dateKey]) || 0;
      return total + Number(value);
    }, 0);

    variables.push({
      rowLetter: refAccount.rowLetter,
      variableValue: variableValue
    });
  });

  // Convert formula to JavaScript and substitute variables
  let jsFormula = formula.toJavaScript(referenceFormula);
  variables.forEach(variable => {
    jsFormula = jsFormula.replaceAll(variable.rowLetter, variable.variableValue.toString());
  });

  // Evaluate the formula
  try {
    const evaluatedAmount = evaluate(jsFormula);
    return primarySum + (Number(evaluatedAmount) || 0);
  } catch (error) {
    console.warn('Math evaluation error in calculateReferenceFormula:', error.message);
    console.warn('JS formula:', jsFormula);
    return primarySum;
  }
};

/**
 * Calculates change for a specific account using different operators
 * @param {string} accountKey - The account identifier
 * @param {string} operator - The function operator type
 * @param {number|string} operatorValue - The operator value
 * @param {string[]} dateList - Array of date strings
 * @param {Object} changeDates - Object containing date values for accounts
 * @param {Object[]} referenceAccounts - Array of reference account objects
 * @param {string} referenceFormula - The formula to evaluate
 * @returns {number} Calculated change amount
 */
export const calculateAccountChange = (accountKey, operator, operatorValue, dateList, changeDates, referenceAccounts, referenceFormula) => {
  const accountTotal = calculateAccountTotal(accountKey, dateList, changeDates);
  
  switch (operator) {
    case 'absoluteValue':
      return Number(operatorValue) * dateList.length || 0;
      
    case 'percentage':
      const percentageMultiplier = (Number(operatorValue) || 0) / 100;
      return accountTotal * percentageMultiplier;
      
    case 'referenceAccount':
      const evaluatedAmount = evaluateReferenceFormula(accountTotal, dateList, referenceAccounts, referenceFormula);
      return evaluatedAmount - accountTotal;
      
    default:
      return accountTotal;
  }
};

/**
 * Calculates standard change (non-reference account) for row data
 * @param {Object} rowData - The row data object
 * @param {string} operator - The function operator type
 * @param {number|string} operatorValue - The operator value
 * @param {string[]} dateList - Array of date strings
 * @returns {number} Calculated standard change
 */
export const calculateStandardChange = (rowData, operator, operatorValue, dateList) => {
  switch (operator) {
    case 'absoluteValue':
      return Number(operatorValue) * dateList.length || 0;
      
    case 'percentage':
      return dateList.reduce((total, month) => {
        const monthValue = Number(rowData[month]) || 0;
        const percentageChange = monthValue * ((Number(operatorValue) || 0) / 100);
        return total + percentageChange;
      }, 0);
      
    default:
      return 0;
  }
};

/**
 * Calculates reference account change
 * @param {string} accountId - The account identifier
 * @param {string[]} dateList - Array of date strings
 * @param {Object} changeDates - Object containing date values for accounts
 * @param {Object[]} referenceAccounts - Array of reference account objects
 * @param {string} referenceFormula - The formula to evaluate
 * @returns {number} Reference account change amount
 */
export const calculateReferenceAccountChange = (accountId, dateList, changeDates, referenceAccounts, referenceFormula) => {
  const accountTotal = calculateAccountTotal(accountId, dateList, changeDates);
  const evaluatedAmount = evaluateReferenceFormula(accountTotal, dateList, referenceAccounts, referenceFormula);
  return evaluatedAmount - accountTotal;
};

/**
 * Calculates change for child accounts
 * @param {string[]} childAccounts - Array of child account keys
 * @param {string} operator - The function operator type
 * @param {number|string} operatorValue - The operator value
 * @param {string[]} dateList - Array of date strings
 * @param {Object} changeDates - Object containing date values for accounts
 * @param {Object[]} referenceAccounts - Array of reference account objects
 * @param {string} referenceFormula - The formula to evaluate
 * @returns {number} Total change for all child accounts
 */
export const calculateChildAccountsChange = (childAccounts, operator, operatorValue, dateList, changeDates, referenceAccounts, referenceFormula) => {
  return childAccounts.reduce((total, accountKey) => {
    const accountChange = calculateAccountChange(accountKey, operator, operatorValue, dateList, changeDates, referenceAccounts, referenceFormula);
    return total + accountChange;
  }, 0);
};

/**
 * Calculates change for the main row account
 * @param {Object} rowData - The row data object
 * @param {string} operator - The function operator type
 * @param {number|string} operatorValue - The operator value
 * @param {string[]} dateList - Array of date strings
 * @param {Object} changeDates - Object containing date values for accounts
 * @param {Object[]} referenceAccounts - Array of reference account objects
 * @param {string} referenceFormula - The formula to evaluate
 * @returns {number} Row change amount
 */
export const calculateRowChange = (rowData, operator, operatorValue, dateList, changeDates, referenceAccounts, referenceFormula) => {
  if (operator === 'referenceAccount' || operator === 'calculatedAccount' || operator === 'recursiveCalculatedAccount') {
    return calculateReferenceAccountChange(rowData.id, dateList, changeDates, referenceAccounts, referenceFormula);
  }
  
  return calculateStandardChange(rowData, operator, operatorValue, dateList);
};

/**
 * Calculates the total value for a change column in AG Grid.
 * @param {Object} params   - AG Grid parameters for a row
 * @param {Object} change   - Change configuration object
 * @param {Object} [dateRange] - Optional active date range filter { from: {year, month}, to: {year, month} }
 *                               When provided, only months within [from, to] are summed.
 * @returns {string|number} Total value or empty string if no changes apply
 */
export const changeTotal = (params, change, dateRange) => {
  // Early validation
  if (!params?.data || !change) {
    return '';
  }

  const rowData = params.data;

  // ── Date-range-filtered path ──────────────────────────────────────────────
  // When a date range filter is active we skip the pre-stored totals (which always
  // cover the full change date range) and instead sum the per-month fields that are
  // already stamped onto the row, keeping only months within [fromNum, toNum].
  if (dateRange) {
    const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const fromNum = dateRange.from.year * 100 + dateRange.from.month;
    const toNum   = dateRange.to.year   * 100 + dateRange.to.month;

    // Determine which month-keyed field prefix this change uses:
    //   dominant changes store per-month values as  "${change.id} ${month}"
    //   all other change types store as              "${change.change_label} ${month}"
    const prefix = change.is_dominant_change ? change.id : change.change_label;

    // Walk all keys on the row looking for "<prefix> MMM yyyy" fields
    let filteredTotal = 0;
    let foundAny = false;
    Object.keys(rowData).forEach(key => {
      if (!key.startsWith(prefix + ' ')) return;
      const monthPart = key.slice(prefix.length + 1); // "MMM yyyy"
      const m = monthPart.match(/^([A-Za-z]{3}) (\d{4})$/);
      if (!m) return;
      const idx = monthNames.indexOf(m[1]);
      if (idx === -1) return;
      const n = Number(m[2]) * 100 + (idx + 1);
      if (n < fromNum || n > toNum) return;
      const val = Number(rowData[key]) || 0;
      if (val !== 0) foundAny = true;
      filteredTotal += val;
    });

    // Return empty string (not 0) when no months matched — consistent with the
    // unfiltered path's "no accounts selected" return value.
    return foundAny ? filteredTotal : '';
  }

  // ── Unfiltered path (original behaviour) ─────────────────────────────────

  // For dominant changes, check the id-keyed adjusted total first
  // (stored by calculateDominantChanges / dominant second pass as ${change.id} total)
  if (change.is_dominant_change) {
    const dominantTotalField = `${change.id} total`;
    if (rowData[dominantTotalField] !== undefined && rowData[dominantTotalField] !== null) {
      return rowData[dominantTotalField];
    }
  }

  // Check if the row already has a pre-calculated total field
  // This handles Master Stat changes and other pre-calculated scenarios
  const totalFieldName = `${change.change_label} total`;
  if (rowData[totalFieldName] !== undefined && rowData[totalFieldName] !== null) {
    return rowData[totalFieldName];
  }
  
  const {
    function_operator: operator,
    operator_value: operatorValue,
    reference_accounts: referenceAccounts,
    ref_account_formula: referenceFormula,
    dates: changeDates,
    selected_accounts: selectedAccounts,
    date_range: changeDateRange
  } = change;  

  // Generate date range for calculations
  const dateList = getMonthsBetweenDates(changeDateRange[0], changeDateRange[1]);

  // Check if current row is selected
  let isRowSelected = selectedAccounts.includes(rowData.id);

  // If not in selected_accounts, check if it has monthly_values in reference_accounts
  if (!isRowSelected && referenceAccounts && Array.isArray(referenceAccounts)) {
    const hasMonthlyValues = referenceAccounts.some(
      refAcct => refAcct.glKey === rowData.id &&
                refAcct.monthly_values &&
                refAcct.monthly_values.length > 0
    );
    if (hasMonthlyValues) {
      isRowSelected = true;
    }
  }

  // Find child accounts that match selected accounts
  const matchingChildAccounts = (rowData.child_accounts || [])
    .filter(glKey => selectedAccounts.includes(glKey));

  // Calculate total based on different scenarios
  let totalChange = 0;

  if (isRowSelected) {
    // For accounts with monthly_values, sum them up directly
    const refAccount = referenceAccounts && referenceAccounts.find(ref => ref.glKey === rowData.id);
    if (refAccount && refAccount.monthly_values && refAccount.monthly_values.length > 0) {
      totalChange = refAccount.monthly_values.reduce((sum, mv) => sum + (mv.value || 0), 0);
    } else {
      totalChange += calculateRowChange(rowData, operator, operatorValue, dateList, changeDates, referenceAccounts, referenceFormula);
    }
  }

  if (matchingChildAccounts.length > 0) {
    totalChange += calculateChildAccountsChange(matchingChildAccounts, operator, operatorValue, dateList, changeDates, referenceAccounts, referenceFormula);
  }

  // Return result only if any accounts were selected
  const hasSelectedAccounts = isRowSelected || matchingChildAccounts.length > 0;
  return hasSelectedAccounts ? (totalChange || 0) : '';
};

/**
 * @param {String} month 'MMM YYYY' format
 * @param {Object} params AG Grid parameters for a row
 * @param {Array} changes Array of change objects
 * @returns {Number} The final forecasted value for that month
 */
export const forecastPeriodMonth = (month, params, changes) => {
  const data = params.data;
  if (!data) {
    return 0;
  }

  const monthDate = parse(month, 'MMM yyyy', new Date());

  let basePeriod = data[month];
  if (typeof basePeriod === 'undefined') {
    const dateKey = format(monthDate, 'yyyy-MM-dd');
    basePeriod = data[dateKey];
  }
  basePeriod = Number(basePeriod) || 0;

  if (!changes || changes.length === 0) {
    return basePeriod;
  }

  // Parent placeholder rows (hasChildren:true, id is not a GL key) have their
  // change deltas pre-computed and stored as `${change_label} ${month}` at load
  // time. We can't recalculate them live because their id is not in selected_accounts.
  // Apply the same standard-first, dominant-last logic using the pre-stored deltas.
  if (data.hasChildren) {
    const standardChanges = changes.filter(c => !c.is_dominant_change);
    const dominantChanges = changes.filter(c => c.is_dominant_change);

    let standardDelta = 0;
    standardChanges.forEach((change) => {
      const changeStart = parse(change.date_range[0], 'yyyy-MM-dd', new Date());
      const changeEnd = parse(change.date_range[1], 'yyyy-MM-dd', new Date());
      if (monthDate >= changeStart && monthDate <= changeEnd) {
        const storedDelta = data[`${change.change_label} ${month}`];
        if (storedDelta !== undefined && storedDelta !== null) {
          standardDelta += Number(storedDelta);
        }
      }
    });

    let dominantDelta = 0;
    dominantChanges.forEach((change) => {
      const changeStart = parse(change.date_range[0], 'yyyy-MM-dd', new Date());
      const changeEnd = parse(change.date_range[1], 'yyyy-MM-dd', new Date());
      if (monthDate >= changeStart && monthDate <= changeEnd) {
        // Parent rows store the raw entry delta under change_label key.
        // Dominant column value = entryDelta - standardDelta so forecast nets correctly.
        const storedEntryDelta = data[`${change.change_label} ${month}`];

        if (storedEntryDelta !== undefined && storedEntryDelta !== null) {
          dominantDelta += Number(storedEntryDelta) - standardDelta;
        }
      }
    });

    return basePeriod + standardDelta + dominantDelta;
  }

  // Separate standard and dominant changes so dominant changes are always
  // applied last, after all standard deltas are known.
  const standardChanges = changes.filter(c => !c.is_dominant_change);
  const dominantChanges = changes.filter(c => c.is_dominant_change);

  // --- Step 1: sum all standard change deltas ---
  let standardDelta = 0;
  let anyChangeApplied = false;

  standardChanges.forEach((change) => {
    const changeStart = parse(change.date_range[0], 'yyyy-MM-dd', new Date());
    const changeEnd = parse(change.date_range[1], 'yyyy-MM-dd', new Date());

    if (monthDate >= changeStart && monthDate <= changeEnd) {
      const increment = getSingleMonthChange(month, params, change);
      if (increment !== '') {
        standardDelta += increment;
        anyChangeApplied = true;
      }
    }
  });

  // --- Step 2: apply dominant changes ---
  // The dominant change column displays the adjusted delta needed to reach the target
  // forecast (base + entryDelta), offsetting all standard changes.
  // Column value = entryDelta - standardDelta (e.g. +7% when entry=+4%, standard=-3%)
  // Forecast = base + standardDelta + dominantColumnValue = base + entryDelta ✓
  let dominantDelta = 0;
  let hasDominant = false;

  dominantChanges.forEach((change) => {
    const changeStart = parse(change.date_range[0], 'yyyy-MM-dd', new Date());
    const changeEnd = parse(change.date_range[1], 'yyyy-MM-dd', new Date());

    if (monthDate >= changeStart && monthDate <= changeEnd) {
      // getSingleMonthChange returns the pre-stored adjusted column value
      // (written by calculateDominantChanges as entryDelta - standardDelta)
      const preStoredKey = `${change.id} ${month}`;
      const preStored = data[preStoredKey];

      if (preStored !== undefined && preStored !== null) {
        // Use the pre-stored adjusted delta directly
        dominantDelta += Number(preStored);
        hasDominant = true;
        anyChangeApplied = true;
      } else {
        // Not yet pre-computed (rows just loaded) — compute live.
        // NOTE: standardDelta here only reflects changes processed before this
        // dominant change in the current loop iteration. If changes are stored in
        // a different order than they were applied, this fallback can produce an
        // incorrect delta. The pre-stored path is authoritative; this is a
        // best-effort fallback only.
        debugLogger.warn('FORECAST', 'Dominant change not pre-computed, falling back to live calculation — result may be approximate', {
          changeId: change.id,
          changeLabel: change.change_label,
          month,
        });
        const { function_operator: op, operator_value: opVal, dates: changeDates,
                selected_accounts: selectedAccounts, reference_accounts: refAccounts,
                ref_account_formula: refFormula } = change;
        const entryDelta = calculateSingleMonthChange(
          month, data.id, [], data, selectedAccounts, op, opVal, changeDates, refAccounts, refFormula
        );
        if (entryDelta !== '') {
          dominantDelta += Number(entryDelta) - standardDelta;
          anyChangeApplied = true;
        }
      }
    }
  });

  if (!anyChangeApplied) {
    return basePeriod;
  }

  // Forecast = base + standard + dominant column value = base + entryDelta
  return basePeriod + standardDelta + dominantDelta;
};

/**
 * @param {Object} params AG Grid parameters for a row
 * @param {Array} changes Array of change objects
 * @returns {Number} The total forecasted value over the entire forecast period
 */
export const forecastPeriodTotal = (params, changes, dateRange) => {
  const data = params.data;
  if (!data) {
    return 0;
  }

 
  const dateRegex = /^[A-Za-z]{3} \d{4}$/;
  const baseMonths = Object.keys(data).filter((key) => dateRegex.test(key));

  if (baseMonths.length === 0) {
    return 0;
  }

  // If a date range filter is active, compute its numeric bounds for filtering
  const monthNames = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const fromNum = dateRange ? dateRange.from.year * 100 + dateRange.from.month : null;
  const toNum   = dateRange ? dateRange.to.year   * 100 + dateRange.to.month   : null;
  const inRange = (monthStr) => {
    if (fromNum === null) return true;
    const m = monthStr.match(/^([A-Za-z]{3}) (\d{4})$/);
    if (!m) return true;
    const n = Number(m[2]) * 100 + (monthNames.indexOf(m[1]) + 1);
    return n >= fromNum && n <= toNum;
  };

  const baseMonthDates = baseMonths.map(m => parse(m, 'MMM yyyy', new Date())).sort((a, b) => a - b);
  const earliestBase = format(baseMonthDates[0], 'yyyy-MM-dd');
  const latestBase = format(baseMonthDates[baseMonthDates.length - 1], 'yyyy-MM-dd');

  if (!changes || changes.length === 0) {
    let totalNoChanges = 0;
    baseMonths.forEach(m => {
      if (inRange(m)) totalNoChanges += Number(data[m]) || 0;
    });
    return totalNoChanges;
  }

  const allStartDates = changes.map((change) => parse(change.date_range[0], 'yyyy-MM-dd', new Date()));
  const allEndDates = changes.map((change) => parse(change.date_range[1], 'yyyy-MM-dd', new Date()));
  
  const earliestChange = format(new Date(Math.min(...allStartDates)), 'yyyy-MM-dd');
  const latestChange = format(new Date(Math.max(...allEndDates)), 'yyyy-MM-dd');

  const earliestOverall = (earliestChange < earliestBase) ? earliestChange : earliestBase;
  const latestOverall = (latestChange > latestBase) ? latestChange : latestBase;

  const forecastDates = getMonthsBetweenDates(earliestOverall, latestOverall);
  // Apply date range filter to the months we sum
  const filteredDates = forecastDates.filter(inRange);


  let basePeriodTotal = 0;
  filteredDates.forEach((dateStr) => {
    const val = Number(data[dateStr]) || 0;
    basePeriodTotal += val;
  });

  let totalChanges = 0;

  // Delegate to forecastPeriodMonth per month so the Total column uses the same
  // dominant-aware logic as the individual monthly columns.
  filteredDates.forEach((month) => {
    const monthForecast = forecastPeriodMonth(month, params, changes);
    const baseVal = Number(data[month]) || 0;
    totalChanges += monthForecast - baseVal;
  });

  const finalForecastTotal = basePeriodTotal + totalChanges;
  return finalForecastTotal;
};

/**
 * @param {Object} params AG Grid parameters for a row
 * @returns Total of base period and change values for a single month
 */
/**
 * Returns the base-period value for a single month from a reference grid row.
 *
 * Contract: `data.basePeriodDates` must be a plain object keyed in 'yyyy-MM-dd'
 * format (e.g. { "2024-01-01": 50000 }).  Reference grid rows are now normalised
 * to this format by `buildNormalisedRefRow` in changeDetails.jsx, so a single
 * direct lookup is sufficient.  The legacy multi-fallback chain has been removed.
 *
 * @param {Object} params AG Grid cell params
 * @returns {number} Base period value plus any applied change deltas for this month
 */
export const referencePeriodMonth = (params) => {
  if (!params.data) return;

  const data = params.data;
  const month = params.column.userProvidedColDef.headerName; // 'MMM yyyy'
  const dateKey = format(parse(month, 'MMM yyyy', new Date()), 'yyyy-MM-dd');

  const basePeriod = Number(data.basePeriodDates?.[dateKey]) || 0;
  let total = basePeriod;

  const changes = Array.isArray(data.changes) ? data.changes : [];
  changes.forEach((changeId) => {
    total += data[`${changeId} ${month}`] || 0;
  });

  return total;
};

/**
 * Returns the total base-period value across all months in the change date range,
 * plus any applied change deltas.
 *
 * Contract: same as referencePeriodMonth — `data.basePeriodDates` is 'yyyy-MM-dd'-keyed.
 *
 * @param {Object} params AG Grid cell params
 * @param {string[]} dateRange Array of 'MMM yyyy' strings
 * @returns {number}
 */
export const referencePeriodTotal = (params, dateRange) => {
  if (!params.data) return 0;

  const data = params.data;
  let total = 0;

  dateRange.forEach((month) => {
    const dateKey = format(parse(month, 'MMM yyyy', new Date()), 'yyyy-MM-dd');
    total += Number(data.basePeriodDates?.[dateKey]) || 0;
  });

  const changes = Array.isArray(data.changes) ? data.changes : [];
  if (changes.length > 0) {
    dateRange.forEach((month) => {
      changes.forEach((changeId) => {
        total += data[`${changeId} ${month}`] || 0;
      });
    });
  }

  return total;
};

export const accountAgg = (params) => {
  // console.log(params);
}

export const singleMonthGetter = (params) => {
  if(params.data){
    let total = 0;
    const month = params.column.userProvidedColDef.headerName;
    if(typeof params.data[month] !== 'undefined' ){
      total = Number(params.data[month]);
    }

    if(total === 0){
      return '';
    }
    return total;
  }
  return;
}
